import React from 'react';
import { useTranslation } from 'react-i18next';

import GenericTableWithFilters from '../../../components/GenericTableWithFilters/GenericTableWithFilters';
import dateUtils from '../../../lib/dates';
import './ServicesTable.scss';

import {
  ApplicationData,
  AppliedFilter,
  NetworkSegment,
  Policy,
  ServiceData,
} from '../../../models/master';
import images from '../../../images/images.js';

import GenericStatusField from '../../../components/GenericStatusField/GenericStatusField';

import { Filter } from '../../../components/FindAndFilterBar/FindAndFilterBar';
import { Link } from 'react-router-dom';
import GenericTruncateString from '../../../components/GenericTruncateString/GenericTruncateString';
import { Tooltip } from '@carbon/react';
import { setSessionStorageItem } from '../../../lib/utils';
import {
  DEFAULT_NETWORK_SEGMENT_ID,
  defaultNetworkSegmentObject,
} from '../../../lib/constants';

interface ServiceTableRows {
  id: any;
  name:
    | boolean
    | React.ReactChild
    | React.ReactFragment
    | React.ReactPortal
    | null
    | undefined;
  port_id: string;
  protocol: string;
  application_id: string;
  port_protocol: JSX.Element | string;
  application_name: string | JSX.Element;
  associated_policies: JSX.Element | string;
  updated_at: JSX.Element | string;
  allLabels: JSX.Element | null | string;
  network_segment_name: string | JSX.Element;
  port_protocol_field: string | JSX.Element;
}

const ServicesTable = (props: {
  filteredDataCallback: (data: any) => void;
  filtersSelected: any;
  filtersAppliedCallback: (data: any) => void;
  filters: any;
  rows: ServiceData[] | null;
  data: ServiceData[] | null | undefined;
  filteredDataSet: ServiceData[] | null;
  elementCount: number;
  onRefresh: () => void;
  currentPageNumber: number;
  currentPageSize: number;
  onPageChange: (pageData: any) => void;
  sortRows(arg0: unknown, direction: string): void;
  serviceFilterByAppList: string[] | null;
  networkSegmentList: NetworkSegment[] | null;
  persistFilter?: boolean;
  error403Flag: boolean;
  error500Flag: boolean;
  networkSegmentNotAuthorized: boolean;
  leftInlineMultiSelectFilter?: Filter[];
  applicationsData: ApplicationData[] | null;
  loading: boolean;
}) => {
  const { t } = useTranslation('applicationsContainer');
  const { rows, onRefresh } = props;
  const error403Flag = props.error403Flag;
  const error500Flag = props.error500Flag;

  const myheaders = [
    {
      key: 'name',
      header: t('tableHeaders.service'),
      sort: 'sortByName',
      originalKey: 'name',
    },
    {
      key: 'network_segment_name',
      header: t('tableHeaders.networkSegment'),
      sort: 'sortByNetworkSegment',
      originalKey: 'network_segment_name',
    },
    {
      key: 'port_protocol_field',
      originalKey: 'port_protocol_field',
      header: t('tableHeaders.port'),
      sort: 'sortByPortProtocol',
    },
    {
      key: 'application_name',
      originalKey: 'application_name',
      header: t('tableHeaders.application'),
      sort: 'sortByApplication',
    },
    {
      key: 'associated_policies',
      originalKey: 'associated_policies',
      header: t('tableHeaders.connectionPolicies'),
      sort: 'sortByAssociatedPolicies',
    },
    {
      key: 'updated_at',
      originalKey: 'updated_at',
      header: t('tableHeaders.lastUpdated'),
      sort: 'sortByUpdated',
      style: { minWidth: '12.5rem' },
    },
  ];

  const emptyStateData = {
    icon: images.noApplicationsIcon(),
    notFoundIcon: images.NotFoundLarge(),
    emptyStateHeader: t('emptyState.emptyContainerHeader'),
    emptyStateDescription: t('emptyState.emptyContainerDescription'),
  };

  const getNotAuthorizedContent = () => {
    return <GenericStatusField status='notAuthorised'></GenericStatusField>;
  };

  const handlePolicyClick = (svc_name: string) => {
    setSessionStorageItem(
      'MCNM_FILTERS_APPLIED',
      JSON.stringify([
        {
          event: { selectedItem: svc_name },
          key: 'to',
          type: 'single',
        },
      ])
    );
  };

  const getNetworkSegmentName = (id: string) => {
    const networkSegments =
      id === DEFAULT_NETWORK_SEGMENT_ID
        ? defaultNetworkSegmentObject
        : id &&
          Array.isArray(props.networkSegmentList) &&
          props.networkSegmentList.find(
            segments => segments.resource_id === id
          );
    return networkSegments ? networkSegments.name : '—';
  };

  const formatServiceData = (data: ServiceData[] | null | undefined) => {
    return data?.map((item: ServiceData) => ({
      ...item,
      network_segment_name: getNetworkSegmentName(
        item?.network_segment_id || ''
      ),
    }));
  };

  const getApplicationID = (name: string) => {
    const applicationID = props.applicationsData?.find(
      application => application.name === name
    );
    return applicationID?.resource_id;
  };

  const setRowsData = () => {
    let formattedRows: ServiceTableRows[] = [];
    if (error403Flag) return [];
    if (error500Flag) return [];
    if (rows && rows.length === 0) return [];
    if (rows)
      rows.map((row: ServiceData) => {
        const associatedPoliciesName = row.associated_policies?.map(
          policy => policy.name
        );

        formattedRows.push({
          id: row.id,
          name: !!row.name ? (
            row.application_name ? (
              <Link
                className='no-underline-link'
                to={`/applicationDetails?appId=${getApplicationID(
                  row.application_name
                )}`}
              >
                <GenericTruncateString str={row?.name} tableView={true} />
              </Link>
            ) : (
              '—'
            )
          ) : (
            '—'
          ),
          associated_policies: Array.isArray(row.associated_policies) ? (
            <div>
              {row.associated_policies?.length > 0 ? (
                <Tooltip
                  direction='bottom'
                  label={associatedPoliciesName?.map((item, index) => (
                    <span key={index}>
                      {item}
                      <br />
                    </span>
                  ))}
                >
                  <Link
                    className='service-no-underline-link'
                    onClick={() => handlePolicyClick(row.name)}
                    to={`/connectionAccessPolicies`}
                  >
                    <GenericTruncateString
                      str={(row?.associated_policies?.length
                        ? !!row.associated_policies?.length
                          ? row.associated_policies.length
                          : ''
                        : 0
                      ).toString()}
                    />
                  </Link>
                </Tooltip>
              ) : (
                <GenericTruncateString
                  str={(row?.associated_policies?.length
                    ? !!row.associated_policies?.length
                      ? row.associated_policies.length
                      : ''
                    : 0
                  ).toString()}
                />
              )}
            </div>
          ) : (
            '—'
          ),

          updated_at: !!row.updated_at
            ? dateUtils.getUserFriendlyDate(row.updated_at)
            : '—',
          application_name: row?.application_name ? (
            !!row.application_name ? (
              <Link
                className='no-underline-link'
                to={`/applicationDetails?appId=${getApplicationID(
                  row.application_name
                )}`}
              >
                <GenericTruncateString
                  str={row?.application_name}
                  tableView={true}
                />
              </Link>
            ) : (
              '—'
            )
          ) : (
            getNotAuthorizedContent()
          ),
          network_segment_name: !props.networkSegmentNotAuthorized ? (
            <Link
              className='no-underline-link'
              to={`/networkSegmentDetails?nwSegId=${row.network_segment_id}`}
            >
              <GenericTruncateString
                str={getNetworkSegmentName(row?.network_segment_id || '')}
                tableView={true}
              />
            </Link>
          ) : (
            getNotAuthorizedContent()
          ),
          port_id: '',
          protocol: '',
          application_id: '',
          port_protocol: '',
          allLabels: null,
          port_protocol_field: row?.port_protocol_field
            ? !!row.port_protocol_field
              ? row.port_protocol_field
              : '—'
            : getNotAuthorizedContent(),
        });
        return 0;
      });
    else return null;
    return formattedRows;
  };

  return (
    <GenericTableWithFilters
      id='table-with-filter-panel'
      rows={setRowsData()}
      data={props.filteredDataSet}
      headers={myheaders}
      isSortable={true}
      totalElementsCount={props.elementCount ? props.elementCount : 0}
      fullData={formatServiceData(props.data)}
      onTableRefresh={() => onRefresh()}
      filteredDataCallback={(data: ServiceTableRows[]) =>
        props.filteredDataCallback(data)
      }
      selectedFiltersVal={props.filtersSelected as any}
      setFilterApplied={(data: AppliedFilter[]) =>
        props.filtersAppliedCallback(data)
      }
      filters={props.filters}
      currentPageNumber={props.currentPageNumber}
      currentPageSize={props.currentPageSize}
      onPageChange={(pageData: any) => props.onPageChange(pageData)}
      emptyState={emptyStateData}
      sortRows={(data, sortDirection) => props.sortRows(data, sortDirection)}
      persistFilter={props.persistFilter}
      render403Container={props.error403Flag}
      render500Container={props.error500Flag}
      leftInlineMultiSelectFilter={props.leftInlineMultiSelectFilter}
      tableDataLoading={props.loading}
    />
  );
};

export default ServicesTable;
